/**
 * Lightweight API client.
 *
 * Invariants:
 * - `REACT_APP_BACKEND_URL` is the base; every path is mounted under `/api`.
 * - The access token lives only in memory.
 * - Every `Response` body is consumed exactly once via `consumeBody()` —
 *   the helper itself wraps `.text()` in a try/catch so a stream that's
 *   somehow already read returns null rather than crashing the caller.
 * - On 401 we attempt one silent refresh against `/api/auth/refresh`
 *   (sends the httpOnly cookie via `credentials: "include"`) and replay the
 *   request once.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_PREFIX = "/api";

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
let onAuthCleared: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function registerAuthClearedHandler(fn: () => void): void {
  onAuthCleared = fn;
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

function formatDetail(detail: unknown): string {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e: any) =>
        e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)
      )
      .filter(Boolean)
      .join(" ");
  }
  if (typeof detail === "object" && detail) {
    const d = detail as Record<string, unknown>;
    if (typeof d.message === "string") return d.message;
    if (typeof d.msg === "string") return d.msg;
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

interface ParsedResponse {
  status: number;
  ok: boolean;
  data: unknown;
  raw: string | null;
}

/**
 * Fetch + read the body in one shot.
 *
 * Why not return a `Response` and let the caller `.text()` later? Because
 * third-party fetch interceptors (PostHog session recording, in our case)
 * patch `window.fetch` and consume the body for replay, leaving the React
 * caller with a drained stream. By doing `await res.text()` here, immediately
 * after the network completes, we win the race in every case.
 */
async function rawFetchParsed(
  path: string,
  init: RequestInit & { auth?: boolean } = {}
): Promise<ParsedResponse> {
  const url = `${BACKEND_URL}${API_PREFIX}${path}`;
  const headers = new Headers(init.headers || {});
  if (init.body && typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (init.auth !== false && accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  // Use the pristine fetch we stashed in index.html before any third-party
  // scripts could monkey-patch it (PostHog session recording, etc.).
  const fetchFn: typeof fetch =
    (typeof window !== "undefined" && (window as any).__rawFetch) || fetch;
  const res = await fetchFn(url, {
    ...init,
    headers,
    credentials: "include",
  });
  let raw: string | null = null;
  if (res.status !== 204) {
    try { raw = await res.text(); }
    catch { raw = null; }
  }
  let data: unknown = null;
  if (raw && raw.length > 0) {
    try { data = JSON.parse(raw); }
    catch { data = raw; }
  }
  return { status: res.status, ok: res.ok, data, raw };
}

async function attemptRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const parsed = await rawFetchParsed("/auth/refresh", {
          method: "POST",
          auth: false,
        });
        if (!parsed.ok) return null;
        const data = parsed.data as { access_token?: string } | null;
        if (!data || typeof data.access_token !== "string") return null;
        accessToken = data.access_token;
        return accessToken;
      } catch {
        return null;
      } finally {
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      }
    })();
  }
  return refreshPromise;
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit & { auth?: boolean } = {}
): Promise<T> {
  // First attempt — fetch and parse in one shot.
  let parsed = await rawFetchParsed(path, init);

  // If unauthorized and the request opts into auth, try one silent refresh
  // and replay.
  if (parsed.status === 401 && init.auth !== false) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      parsed = await rawFetchParsed(path, init);
    } else if (onAuthCleared) {
      accessToken = null;
      onAuthCleared();
    }
  }

  if (parsed.status === 204) return undefined as unknown as T;

  if (!parsed.ok) {
    const detail =
      parsed.data && typeof parsed.data === "object" && "detail" in (parsed.data as any)
        ? (parsed.data as any).detail
        : parsed.data;
    throw new ApiError(parsed.status, formatDetail(detail), detail);
  }

  return parsed.data as T;
}

export const api = {
  get: <T = unknown>(path: string) => apiFetch<T>(path, { method: "GET" }),
  post: <T = unknown>(
    path: string,
    body?: unknown,
    opts: { auth?: boolean } = {}
  ) =>
    apiFetch<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      auth: opts.auth,
    }),
};
